import React, { useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Shield, Puzzle, Activity, CreditCard, Database, Sparkles, LifeBuoy, Search, Plus, MoreVertical, Check, X, ExternalLink, Download } from 'lucide-react';
import { AppShell } from '../components/AppShell';
type TabId = 'usuarios' | 'permisos' | 'integraciones' | 'auditoria' | 'facturacion' | 'fuentes' | 'premium' | 'soporte';
// --- Shared Components ---
function Toggle({
  checked,
  onChange
}: {
  checked: boolean;
  onChange: () => void;
}) {
  return <button type="button" onClick={onChange} className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${checked ? 'bg-brand-500' : 'bg-slate-200'}`} data-id="element-171">
      <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${checked ? 'translate-x-4' : 'translate-x-0'}`} data-id="element-172" />
    </button>;
}
function StatusBadge({
  status
}: {
  status: 'Activo' | 'Suspendido' | 'Pendiente' | 'Abierto' | 'En progreso' | 'Resuelto' | 'Alta' | 'Media' | 'Baja';
}) {
  const styles = {
    Activo: 'bg-emerald-50 text-emerald-600',
    Suspendido: 'bg-rose-50 text-rose-600',
    Pendiente: 'bg-amber-50 text-amber-600',
    Abierto: 'bg-rose-50 text-rose-600',
    'En progreso': 'bg-amber-50 text-amber-600',
    Resuelto: 'bg-emerald-50 text-emerald-600',
    Alta: 'bg-rose-50 text-rose-600',
    Media: 'bg-amber-50 text-amber-600',
    Baja: 'bg-slate-100 text-slate-500'
  };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${styles[status]}`} data-id="element-173">
      {status}
    </span>;
}
// --- Tab Content Components ---
function UsuariosTab() {
  const users = [{
    id: 1,
    name: 'Dr. Diego Rossi',
    email: 'drossi@pharmacorp.com',
    role: 'Admin',
    status: 'Activo' as const,
    lastAccess: 'Hace 2 horas',
    initials: 'DR'
  }, {
    id: 2,
    name: 'María Gómez',
    email: 'mgomez@pharmacorp.com',
    role: 'Editor',
    status: 'Activo' as const,
    lastAccess: 'Ayer',
    initials: 'MG'
  }, {
    id: 3,
    name: 'Juan Pérez',
    email: 'jperez@pharmacorp.com',
    role: 'Viewer',
    status: 'Pendiente' as const,
    lastAccess: 'Nunca',
    initials: 'JP'
  }, {
    id: 4,
    name: 'Ana Silva',
    email: 'asilva@pharmacorp.com',
    role: 'Editor',
    status: 'Activo' as const,
    lastAccess: 'Hace 3 días',
    initials: 'AS'
  }, {
    id: 5,
    name: 'Carlos López',
    email: 'clopez@pharmacorp.com',
    role: 'Viewer',
    status: 'Suspendido' as const,
    lastAccess: 'Hace 2 meses',
    initials: 'CL'
  }];
  return <div className="space-y-4" data-id="element-174">
      <div className="flex items-center justify-between" data-id="element-175">
        <h2 className="text-lg font-semibold text-navy-950" data-id="element-176">
          Gestión de Usuarios
        </h2>
        <button className="flex items-center gap-2 bg-brand-500 hover:bg-brand-400 text-white text-sm font-medium px-4 py-2 rounded-md transition-colors shadow-sm" data-id="element-177">
          <Plus size={16} data-id="element-178" /> Nuevo Usuario
        </button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-card overflow-hidden" data-id="element-179">
        <div className="p-4 border-b border-slate-200 bg-slate-50/50" data-id="element-180">
          <div className="relative max-w-md" data-id="element-181">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} data-id="element-182" />
            <input type="text" placeholder="Buscar usuarios..." className="w-full h-9 pl-9 pr-4 rounded-md border border-slate-200 text-sm outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500" data-id="element-183" />
          </div>
        </div>
        <table className="w-full text-left border-collapse" data-id="element-184">
          <thead data-id="element-185">
            <tr className="border-b border-slate-200 bg-slate-50/50" data-id="element-186">
              <th className="py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-187">
                Usuario
              </th>
              <th className="py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-188">
                Rol
              </th>
              <th className="py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-189">
                Estado
              </th>
              <th className="py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-190">
                Último Acceso
              </th>
              <th className="py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider text-right" data-id="element-191">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-192">
            {users.map(u => <tr key={u.id} className="hover:bg-slate-50 transition-colors" data-id="element-193">
                <td className="py-3 px-4" data-id="element-194">
                  <div className="flex items-center gap-3" data-id="element-195">
                    <div className="w-8 h-8 rounded-full bg-slate-100 text-navy-600 flex items-center justify-center text-xs font-semibold border border-slate-200" data-id="element-196">
                      {u.initials}
                    </div>
                    <div data-id="element-197">
                      <p className="text-sm font-medium text-navy-950" data-id="element-198">
                        {u.name}
                      </p>
                      <p className="text-xs text-navy-600" data-id="element-199">{u.email}</p>
                    </div>
                  </div>
                </td>
                <td className="py-3 px-4 text-sm text-navy-600" data-id="element-200">{u.role}</td>
                <td className="py-3 px-4" data-id="element-201">
                  <StatusBadge status={u.status} data-id="element-202" />
                </td>
                <td className="py-3 px-4 text-sm text-slate-400" data-id="element-203">
                  {u.lastAccess}
                </td>
                <td className="py-3 px-4 text-right" data-id="element-204">
                  <button className="text-slate-400 hover:text-navy-950 p-1" data-id="element-205">
                    <MoreVertical size={16} data-id="element-206" />
                  </button>
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}
function PermisosTab() {
  const permissions = [{
    name: 'Crear alertas',
    admin: true,
    editor: true,
    viewer: false
  }, {
    name: 'Editar alertas',
    admin: true,
    editor: true,
    viewer: false
  }, {
    name: 'Eliminar alertas',
    admin: true,
    editor: false,
    viewer: false
  }, {
    name: 'Realizar búsquedas',
    admin: true,
    editor: true,
    viewer: true
  }, {
    name: 'Invitar usuarios',
    admin: true,
    editor: false,
    viewer: false
  }, {
    name: 'Gestionar integraciones',
    admin: true,
    editor: false,
    viewer: false
  }, {
    name: 'Ver auditoría',
    admin: true,
    editor: false,
    viewer: false
  }, {
    name: 'Gestionar facturación',
    admin: true,
    editor: false,
    viewer: false
  }, {
    name: 'Configurar fuentes',
    admin: true,
    editor: false,
    viewer: false
  }];
  return <div className="space-y-4" data-id="element-207">
      <h2 className="text-lg font-semibold text-navy-950" data-id="element-208">
        Matriz de Permisos
      </h2>
      <div className="bg-white rounded-xl border border-slate-200 shadow-card overflow-hidden" data-id="element-209">
        <table className="w-full text-left border-collapse" data-id="element-210">
          <thead data-id="element-211">
            <tr className="border-b border-slate-200 bg-slate-50/50" data-id="element-212">
              <th className="py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-213">
                Permiso
              </th>
              <th className="py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider text-center" data-id="element-214">
                Admin
              </th>
              <th className="py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider text-center" data-id="element-215">
                Editor
              </th>
              <th className="py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider text-center" data-id="element-216">
                Viewer
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-217">
            {permissions.map((p, i) => <tr key={i} className="hover:bg-slate-50 transition-colors" data-id="element-218">
                <td className="py-3 px-4 text-sm font-medium text-navy-950" data-id="element-219">
                  {p.name}
                </td>
                <td className="py-3 px-4 text-center" data-id="element-220">
                  {p.admin ? <Check size={16} className="text-emerald-500 mx-auto" data-id="element-221" /> : <X size={16} className="text-slate-300 mx-auto" data-id="element-222" />}
                </td>
                <td className="py-3 px-4 text-center" data-id="element-223">
                  {p.editor ? <Check size={16} className="text-emerald-500 mx-auto" data-id="element-224" /> : <X size={16} className="text-slate-300 mx-auto" data-id="element-225" />}
                </td>
                <td className="py-3 px-4 text-center" data-id="element-226">
                  {p.viewer ? <Check size={16} className="text-emerald-500 mx-auto" data-id="element-227" /> : <X size={16} className="text-slate-300 mx-auto" data-id="element-228" />}
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}
function IntegracionesTab() {
  const [integrations, setIntegrations] = useState([{
    id: 1,
    name: 'Salesforce',
    status: true,
    sync: 'Hace 5 min',
    icon: 'SF'
  }, {
    id: 2,
    name: 'Slack',
    status: true,
    sync: 'En tiempo real',
    icon: 'SL'
  }, {
    id: 3,
    name: 'Email / SMTP',
    status: true,
    sync: 'En tiempo real',
    icon: '@'
  }, {
    id: 4,
    name: 'SAP',
    status: false,
    sync: '-',
    icon: 'SAP'
  }, {
    id: 5,
    name: 'HubSpot',
    status: false,
    sync: '-',
    icon: 'HS'
  }]);
  const toggle = (id: number) => {
    setIntegrations(prev => prev.map(i => i.id === id ? {
      ...i,
      status: !i.status
    } : i));
  };
  return <div className="space-y-4" data-id="element-229">
      <h2 className="text-lg font-semibold text-navy-950" data-id="element-230">
        Gestión de Integraciones
      </h2>
      <div className="bg-white rounded-xl border border-slate-200 shadow-card overflow-hidden" data-id="element-231">
        <table className="w-full text-left border-collapse" data-id="element-232">
          <thead data-id="element-233">
            <tr className="border-b border-slate-200 bg-slate-50/50" data-id="element-234">
              <th className="py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-235">
                Integración
              </th>
              <th className="py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-236">
                Última Sincronización
              </th>
              <th className="py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-237">
                Estado
              </th>
              <th className="py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider text-right" data-id="element-238">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-239">
            {integrations.map(i => <tr key={i.id} className="hover:bg-slate-50 transition-colors" data-id="element-240">
                <td className="py-3 px-4" data-id="element-241">
                  <div className="flex items-center gap-3" data-id="element-242">
                    <div className="w-8 h-8 rounded-md bg-slate-100 text-navy-950 flex items-center justify-center text-xs font-bold border border-slate-200" data-id="element-243">
                      {i.icon}
                    </div>
                    <span className="text-sm font-medium text-navy-950" data-id="element-244">
                      {i.name}
                    </span>
                  </div>
                </td>
                <td className="py-3 px-4 text-sm text-slate-400" data-id="element-245">{i.sync}</td>
                <td className="py-3 px-4" data-id="element-246">
                  <Toggle checked={i.status} onChange={() => toggle(i.id)} data-id="element-247" />
                </td>
                <td className="py-3 px-4 text-right" data-id="element-248">
                  <button className="text-xs font-medium text-brand-500 hover:text-brand-600" data-id="element-249">
                    Configurar
                  </button>
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}
function AuditoriaTab() {
  const logs = [{
    id: 1,
    date: '12 Oct 14:30',
    user: 'Dr. Diego Rossi',
    action: 'Crear Alerta',
    detail: 'Creó alerta semántica "Oncológicos"',
    ip: '192.168.1.1',
    type: 'create'
  }, {
    id: 2,
    date: '12 Oct 11:15',
    user: 'María Gómez',
    action: 'Búsqueda',
    detail: 'Buscó "Vademécum PAMI"',
    ip: '192.168.1.4',
    type: 'search'
  }, {
    id: 3,
    date: '11 Oct 09:00',
    user: 'Dr. Diego Rossi',
    action: 'Configuración',
    detail: 'Habilitó integración Salesforce',
    ip: '192.168.1.1',
    type: 'config'
  }, {
    id: 4,
    date: '10 Oct 16:45',
    user: 'Ana Silva',
    action: 'Eliminar Alerta',
    detail: 'Eliminó alerta "Licitaciones"',
    ip: '192.168.1.5',
    type: 'delete'
  }, {
    id: 5,
    date: '10 Oct 10:20',
    user: 'Juan Pérez',
    action: 'Login',
    detail: 'Inicio de sesión exitoso',
    ip: '192.168.1.8',
    type: 'search'
  }];
  const borderColors = {
    create: 'border-l-emerald-500',
    search: 'border-l-brand-500',
    config: 'border-l-amber-500',
    delete: 'border-l-rose-500'
  };
  return <div className="space-y-4" data-id="element-250">
      <div className="flex items-center justify-between" data-id="element-251">
        <h2 className="text-lg font-semibold text-navy-950" data-id="element-252">
          Registro de Auditoría
        </h2>
        <button className="flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-navy-950 transition-colors" data-id="element-253">
          <Download size={16} data-id="element-254" /> Exportar CSV
        </button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-card overflow-hidden" data-id="element-255">
        <div className="p-4 border-b border-slate-200 bg-slate-50/50 flex gap-4" data-id="element-256">
          <select className="h-9 px-3 rounded-md border border-slate-200 text-sm outline-none focus:border-brand-500 bg-white" data-id="element-257">
            <option data-id="element-258">Todos los usuarios</option>
            <option data-id="element-259">Dr. Diego Rossi</option>
            <option data-id="element-260">María Gómez</option>
          </select>
          <select className="h-9 px-3 rounded-md border border-slate-200 text-sm outline-none focus:border-brand-500 bg-white" data-id="element-261">
            <option data-id="element-262">Últimos 7 días</option>
            <option data-id="element-263">Últimos 30 días</option>
          </select>
        </div>
        <table className="w-full text-left border-collapse" data-id="element-264">
          <thead data-id="element-265">
            <tr className="border-b border-slate-200 bg-slate-50/50" data-id="element-266">
              <th className="py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-267">
                Fecha/Hora
              </th>
              <th className="py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-268">
                Usuario
              </th>
              <th className="py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-269">
                Acción
              </th>
              <th className="py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-270">
                Detalle
              </th>
              <th className="py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-271">
                IP
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-272">
            {logs.map(log => <tr key={log.id} className="hover:bg-slate-50 transition-colors" data-id="element-273">
                <td className={`py-3 px-4 text-xs text-slate-500 border-l-2 ${borderColors[log.type as keyof typeof borderColors]}`} data-id="element-274">
                  {log.date}
                </td>
                <td className="py-3 px-4 text-sm font-medium text-navy-950" data-id="element-275">
                  {log.user}
                </td>
                <td className="py-3 px-4 text-xs font-semibold text-navy-600" data-id="element-276">
                  {log.action}
                </td>
                <td className="py-3 px-4 text-sm text-navy-600" data-id="element-277">
                  {log.detail}
                </td>
                <td className="py-3 px-4 text-xs text-slate-400 font-mono" data-id="element-278">
                  {log.ip}
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}
function FacturacionTab() {
  return <div className="space-y-6" data-id="element-279">
      <h2 className="text-lg font-semibold text-navy-950" data-id="element-280">
        Resumen de Facturación
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-281">
        <div className="bg-white rounded-xl border border-slate-200 shadow-card p-6" data-id="element-282">
          <div className="flex items-center justify-between mb-4" data-id="element-283">
            <h3 className="text-sm font-semibold text-navy-950" data-id="element-284">Plan Actual</h3>
            <span className="text-[10px] font-bold uppercase tracking-wider text-brand-600 bg-brand-50 px-2 py-0.5 rounded-full border border-brand-100" data-id="element-285">
              Profesional
            </span>
          </div>
          <div className="flex items-end gap-1 mb-6" data-id="element-286">
            <span className="text-4xl font-light text-navy-950" data-id="element-287">$45.000</span>
            <span className="text-sm text-slate-400 mb-1" data-id="element-288">ARS / mes</span>
          </div>
          <div className="space-y-3 mb-6" data-id="element-289">
            <div className="flex justify-between text-sm" data-id="element-290">
              <span className="text-navy-600" data-id="element-291">Licencias (3/5)</span>
              <span className="font-medium text-navy-950" data-id="element-292">60%</span>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-1.5" data-id="element-293">
              <div className="bg-brand-500 h-1.5 rounded-full" style={{
              width: '60%'
            }} data-id="element-294"></div>
            </div>
          </div>
          <div className="flex gap-3" data-id="element-295">
            <button className="flex-1 bg-navy-950 hover:bg-navy-800 text-white text-sm font-medium py-2 rounded-md transition-colors" data-id="element-296">
              Cambiar Plan
            </button>
            <button className="flex-1 bg-white border border-slate-200 hover:bg-slate-50 text-navy-950 text-sm font-medium py-2 rounded-md transition-colors" data-id="element-297">
              Agregar Asientos
            </button>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-card p-6 flex flex-col" data-id="element-298">
          <h3 className="text-sm font-semibold text-navy-950 mb-4" data-id="element-299">
            Consumo Mensual
          </h3>
          <div className="flex-1 flex items-end justify-between gap-2 pt-4" data-id="element-300">
            {[40, 60, 45, 80, 65, 100].map((h, i) => <div key={i} className="w-full bg-brand-100 rounded-t-sm relative group" data-id="element-301">
                <div className="absolute bottom-0 w-full bg-brand-500 rounded-t-sm transition-all" style={{
              height: `${h}%`
            }} data-id="element-302"></div>
                <div className="opacity-0 group-hover:opacity-100 absolute -top-8 left-1/2 -translate-x-1/2 bg-navy-950 text-white text-xs py-1 px-2 rounded whitespace-nowrap transition-opacity" data-id="element-303">
                  Mes {i + 1}
                </div>
              </div>)}
          </div>
          <div className="flex justify-between text-xs text-slate-400 mt-2 pt-2 border-t border-slate-100" data-id="element-304">
            <span data-id="element-305">Hace 6 meses</span>
            <span data-id="element-306">Mes actual</span>
          </div>
        </div>
      </div>
    </div>;
}
function FuentesTab() {
  const [sources, setSources] = useState([{
    id: 1,
    name: 'ANMAT',
    desc: 'Disposiciones y normativas',
    status: true,
    docs: '145.2K',
    premium: false
  }, {
    id: 2,
    name: 'Boletín Oficial',
    desc: 'Sección 1 y 2 completa',
    status: true,
    docs: '2.4M',
    premium: false
  }, {
    id: 3,
    name: 'SENASA',
    desc: 'Resoluciones agroalimentarias',
    status: true,
    docs: '89.5K',
    premium: false
  }, {
    id: 4,
    name: 'Ministerio de Salud',
    desc: 'Resoluciones y circulares',
    status: true,
    docs: '112.1K',
    premium: false
  }, {
    id: 5,
    name: 'PAMI',
    desc: 'Vademécum y normativas',
    status: false,
    docs: '45.3K',
    premium: true
  }, {
    id: 6,
    name: 'INAME',
    desc: 'Instituto Nacional de Medicamentos',
    status: false,
    docs: '34.8K',
    premium: true
  }]);
  const toggle = (id: number) => {
    setSources(prev => prev.map(s => s.id === id ? {
      ...s,
      status: !s.status
    } : s));
  };
  return <div className="space-y-4" data-id="element-307">
      <h2 className="text-lg font-semibold text-navy-950" data-id="element-308">
        Fuentes de Datos Activas
      </h2>
      <div className="bg-white rounded-xl border border-slate-200 shadow-card overflow-hidden" data-id="element-309">
        <table className="w-full text-left border-collapse" data-id="element-310">
          <thead data-id="element-311">
            <tr className="border-b border-slate-200 bg-slate-50/50" data-id="element-312">
              <th className="py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-313">
                Fuente
              </th>
              <th className="py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-314">
                Documentos Indexados
              </th>
              <th className="py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-315">
                Estado
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-316">
            {sources.map(s => <tr key={s.id} className="hover:bg-slate-50 transition-colors" data-id="element-317">
                <td className="py-3 px-4" data-id="element-318">
                  <div className="flex items-center gap-2" data-id="element-319">
                    <span className="text-sm font-medium text-navy-950" data-id="element-320">
                      {s.name}
                    </span>
                    {s.premium && <span className="text-[10px] font-bold uppercase tracking-wider text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-sm" data-id="element-321">
                        Premium
                      </span>}
                  </div>
                  <p className="text-xs text-navy-600" data-id="element-322">{s.desc}</p>
                </td>
                <td className="py-3 px-4 text-sm text-slate-500" data-id="element-323">{s.docs}</td>
                <td className="py-3 px-4" data-id="element-324">
                  <Toggle checked={s.status} onChange={() => toggle(s.id)} data-id="element-325" />
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}
function PremiumTab() {
  const [features, setFeatures] = useState([{
    id: 1,
    name: 'Búsqueda semántica contextual (IA)',
    desc: 'Permite a los usuarios buscar por contexto en lenguaje natural.',
    status: true
  }, {
    id: 2,
    name: 'Alertas semánticas',
    desc: 'Matching inteligente que entiende el significado, no solo palabras clave.',
    status: true
  }, {
    id: 3,
    name: 'API REST & Webhooks',
    desc: 'Acceso programático a la plataforma para integraciones custom.',
    status: false
  }, {
    id: 4,
    name: 'Integraciones avanzadas',
    desc: 'Habilita conectores ERP (SAP, Odoo) y Analytics (Power BI).',
    status: false
  }, {
    id: 5,
    name: 'Auditoría detallada',
    desc: 'Retención de logs por 5 años y exportación avanzada.',
    status: true
  }]);
  const toggle = (id: number) => {
    setFeatures(prev => prev.map(f => f.id === id ? {
      ...f,
      status: !f.status
    } : f));
  };
  return <div className="space-y-4" data-id="element-326">
      <div className="flex items-center gap-2 mb-6" data-id="element-327">
        <Sparkles className="text-amber-500" size={20} data-id="element-328" />
        <h2 className="text-lg font-semibold text-navy-950" data-id="element-329">
          Funcionalidades Premium
        </h2>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-card divide-y divide-slate-100" data-id="element-330">
        {features.map(f => <div key={f.id} className="p-5 flex items-center justify-between hover:bg-slate-50 transition-colors" data-id="element-331">
            <div className="pr-8" data-id="element-332">
              <h3 className="text-sm font-medium text-navy-950 mb-1" data-id="element-333">
                {f.name}
              </h3>
              <p className="text-xs text-navy-600" data-id="element-334">{f.desc}</p>
            </div>
            <Toggle checked={f.status} onChange={() => toggle(f.id)} data-id="element-335" />
          </div>)}
      </div>
    </div>;
}
function SoporteTab() {
  const tickets = [{
    id: 'T-1042',
    subject: 'Problema con integración Salesforce',
    status: 'En progreso' as const,
    priority: 'Alta' as const,
    date: 'Hoy',
    assigned: 'Soporte N2'
  }, {
    id: 'T-1041',
    subject: 'Duda sobre facturación anual',
    status: 'Resuelto' as const,
    priority: 'Media' as const,
    date: 'Ayer',
    assigned: 'Billing'
  }, {
    id: 'T-1038',
    subject: 'Solicitud de nueva fuente: INCUCAI',
    status: 'Abierto' as const,
    priority: 'Baja' as const,
    date: 'Hace 3 días',
    assigned: 'Producto'
  }];
  return <div className="space-y-4" data-id="element-336">
      <div className="flex items-center justify-between" data-id="element-337">
        <h2 className="text-lg font-semibold text-navy-950" data-id="element-338">
          Tickets de Soporte
        </h2>
        <button className="flex items-center gap-2 bg-white border border-slate-200 hover:bg-slate-50 text-navy-950 text-sm font-medium px-4 py-2 rounded-md transition-colors shadow-sm" data-id="element-339">
          <Plus size={16} data-id="element-340" /> Nuevo Ticket
        </button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-card overflow-hidden" data-id="element-341">
        <table className="w-full text-left border-collapse" data-id="element-342">
          <thead data-id="element-343">
            <tr className="border-b border-slate-200 bg-slate-50/50" data-id="element-344">
              <th className="py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-345">
                ID
              </th>
              <th className="py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-346">
                Asunto
              </th>
              <th className="py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-347">
                Estado
              </th>
              <th className="py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-348">
                Prioridad
              </th>
              <th className="py-3 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-349">
                Fecha
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-350">
            {tickets.map(t => <tr key={t.id} className="hover:bg-slate-50 transition-colors cursor-pointer" data-id="element-351">
                <td className="py-3 px-4 text-xs font-mono text-slate-500" data-id="element-352">
                  {t.id}
                </td>
                <td className="py-3 px-4 text-sm font-medium text-navy-950" data-id="element-353">
                  {t.subject}
                </td>
                <td className="py-3 px-4" data-id="element-354">
                  <StatusBadge status={t.status} data-id="element-355" />
                </td>
                <td className="py-3 px-4" data-id="element-356">
                  <StatusBadge status={t.priority} data-id="element-357" />
                </td>
                <td className="py-3 px-4 text-sm text-slate-400" data-id="element-358">{t.date}</td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}
// --- Main Page Component ---
export function AdminPage() {
  const [activeTab, setActiveTab] = useState<TabId>('usuarios');
  const tabs: {
    id: TabId;
    label: string;
    icon: React.ElementType;
  }[] = [{
    id: 'usuarios',
    label: 'Usuarios',
    icon: Users
  }, {
    id: 'permisos',
    label: 'Permisos',
    icon: Shield
  }, {
    id: 'integraciones',
    label: 'Integraciones',
    icon: Puzzle
  }, {
    id: 'auditoria',
    label: 'Auditoría',
    icon: Activity
  }, {
    id: 'facturacion',
    label: 'Facturación',
    icon: CreditCard
  }, {
    id: 'fuentes',
    label: 'Fuentes de Datos',
    icon: Database
  }, {
    id: 'premium',
    label: 'Premium',
    icon: Sparkles
  }, {
    id: 'soporte',
    label: 'Soporte',
    icon: LifeBuoy
  }];
  return <AppShell data-id="element-359">
      <div className="p-6 lg:p-8 max-w-7xl mx-auto h-full flex flex-col" data-id="element-360">
        <div className="mb-6" data-id="element-361">
          <h1 className="text-2xl text-navy-950 mb-1" data-id="element-362">
            Panel de Administración
          </h1>
          <p className="text-sm text-navy-600" data-id="element-363">
            Gestione toda la configuración de su organización.
          </p>
        </div>

        <div className="flex flex-col md:flex-row gap-8 flex-1 min-h-0" data-id="element-364">
          {/* Vertical Tabs */}
          <div className="w-full md:w-56 shrink-0 flex flex-col gap-1" data-id="element-365">
            {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${isActive ? 'bg-white text-brand-600 shadow-sm border border-slate-200' : 'text-navy-600 hover:bg-slate-200/50 hover:text-navy-950 border border-transparent'}`} data-id="element-366">
                  <Icon size={18} className={isActive ? 'text-brand-500' : 'text-slate-400'} data-id="element-367" />
                  {tab.label}
                </button>;
          })}
          </div>

          {/* Tab Content Area */}
          <div className="flex-1 bg-slate-50/50 rounded-2xl border border-slate-200 p-6 overflow-y-auto" data-id="element-368">
            <AnimatePresence mode="wait" data-id="element-369">
              <motion.div key={activeTab} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.2
            }} data-id="element-370">
                {activeTab === 'usuarios' && <UsuariosTab data-id="element-371" />}
                {activeTab === 'permisos' && <PermisosTab data-id="element-372" />}
                {activeTab === 'integraciones' && <IntegracionesTab data-id="element-373" />}
                {activeTab === 'auditoria' && <AuditoriaTab data-id="element-374" />}
                {activeTab === 'facturacion' && <FacturacionTab data-id="element-375" />}
                {activeTab === 'fuentes' && <FuentesTab data-id="element-376" />}
                {activeTab === 'premium' && <PremiumTab data-id="element-377" />}
                {activeTab === 'soporte' && <SoporteTab data-id="element-378" />}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </AppShell>;
}